import { forwardRef, useEffect, useRef, useState } from "react";
import { Wifi, Users, DoorOpen, Sun, MapPin, ChevronDown, CheckCircle2, Circle } from "lucide-react";
import { fmtCurrency } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function imgSrc(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

function PriceRow({ label, value, base, list, highlight, selectable, selected, onSelect, testid }) {
  const save = base && value ? Math.round((1 - value / base) * 100) : 0;
  const discounted = list != null && value != null && list > value;
  const Price = ({ className }) => (
    <span className={`font-mono ${className}`}>
      {discounted && <span className="line-through text-brand-tertiary mr-1.5 text-xs">{fmtCurrency(list)}</span>}
      {value != null ? `${fmtCurrency(value)}/mo` : "—"}
    </span>
  );
  if (selectable) {
    return (
      <button type="button" onClick={onSelect} data-testid={testid}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border text-left transition-[background-color,border-color] ${selected ? "border-brand-gold bg-brand-gold/15 ring-1 ring-brand-gold" : "border-brand-border hover:bg-brand-hover"}`}>
        <span className="flex items-center gap-2">
          {selected ? <CheckCircle2 className="h-4 w-4 text-brand-gold shrink-0" /> : <Circle className="h-4 w-4 text-brand-tertiary shrink-0" />}
          <span className={`text-[11px] uppercase tracking-[0.12em] ${selected ? "text-brand-ink font-medium" : "text-brand-tertiary"}`}>{label}</span>
          {save > 0 && <span className="text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-gold text-brand-ink">Save {save}%</span>}
        </span>
        <Price className={`text-sm ${selected ? "text-brand-ink font-semibold" : "text-brand-ink"}`} />
      </button>
    );
  }
  return (
    <div className={`flex items-center justify-between px-3 py-2 ${highlight ? "bg-brand-gold/12 rounded-md" : "border-b border-brand-border last:border-0"}`}>
      <span className="flex items-center gap-2">
        <span className={`text-[11px] uppercase tracking-[0.12em] ${highlight ? "text-brand-ink font-medium" : "text-brand-tertiary"}`}>{label}</span>
        {save > 0 && <span className={`text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${highlight ? "bg-brand-gold text-brand-ink" : "bg-brand-hover text-brand-muted"}`}>Save {save}%</span>}
      </span>
      <Price className={highlight ? "text-brand-ink text-lg font-semibold" : "text-brand-ink text-sm"} />
    </div>
  );
}

function OfficeCard({ o, fallback, onSeen, selectedTerm, onToggleTerm }) {
  const ref = useRef(null);
  const photo = o.photo_url || fallback;
  const selectable = !!onToggleTerm;
  const terms = [
    { key: "m2m", label: "Month-to-Month", value: o.monthly_price, list: o.list_monthly_price },
    { key: "6mo", label: "6-Month term", value: o.price_6mo, list: o.list_price_6mo },
    { key: "12mo", label: "12-Month term", value: o.price_12mo, list: o.list_price_12mo },
  ].filter((t) => t.value != null);
  useEffect(() => {
    if (!onSeen || !ref.current) return;
    let done = false;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !done) { done = true; onSeen(o.office_number); obs.disconnect(); }
      });
    }, { threshold: 0.5 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [onSeen, o.office_number]);
  return (
    <div ref={ref} className={`group bg-brand-page border shadow-[0_2px_14px_rgba(20,20,20,0.05)] hover:shadow-[0_10px_30px_rgba(20,20,20,0.12)] hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300 break-inside-avoid ${selectedTerm ? "border-brand-gold ring-1 ring-brand-gold" : "border-brand-border"}`} data-testid={`proposal-office-${o.office_number}`}>
      {photo ? (
        <div className="aspect-[4/3] overflow-hidden border-b border-brand-border bg-brand-hover">
          <img src={imgSrc(photo)} alt={`Office ${o.office_number}`} crossOrigin="anonymous" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      ) : (
        <div className="aspect-[4/3] border-b border-brand-border bg-brand-ink flex items-center justify-center">
          <span className="h-9 w-9 border border-brand-gold text-brand-gold flex items-center justify-center font-serif">B</span>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-serif text-2xl text-brand-ink">Office {o.office_number}</p>
          {selectedTerm ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-brand-gold" data-testid={`proposal-office-selected-${o.office_number}`}><CheckCircle2 className="h-3 w-3" /> Selected</span>
          ) : (o.window && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-brand-gold"><Sun className="h-3 w-3" /> Window</span>)}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[12px] text-brand-muted">
          {o.desks != null && <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-brand-tertiary" /> {o.desks} desk{o.desks === 1 ? "" : "s"}</span>}
          {o.conference_credits != null && <span className="inline-flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5 text-brand-tertiary" /> {o.conference_credits} conf. credit{o.conference_credits === 1 ? "" : "s"}</span>}
        </div>
        {selectable && <p className="mt-4 mb-1.5 text-[11px] text-brand-tertiary">Tap a term to request this office at that rate</p>}
        <div className={selectable ? "mt-1 space-y-1.5" : "mt-4 space-y-0.5"}>
          {selectable
            ? terms.map((t) => (
                <PriceRow key={t.key} label={t.label} value={t.value} list={t.list} base={t.key !== "m2m" ? o.monthly_price : null}
                  selectable selected={selectedTerm === t.key} onSelect={() => onToggleTerm(o, t)}
                  testid={`proposal-term-${o.office_number}-${t.key}`} />
              ))
            : (<>
                <PriceRow label="Month-to-Month" value={o.monthly_price} list={o.list_monthly_price} />
                <PriceRow label="6-Month term" value={o.price_6mo} list={o.list_price_6mo} base={o.monthly_price} />
                <PriceRow label="12-Month term" value={o.price_12mo} list={o.list_price_12mo} base={o.monthly_price} highlight />
              </>)}
        </div>
      </div>
    </div>
  );
}

function LocationSection({ block, index, onOfficeSeen, selection, onToggleTerm }) {
  const [fpOpen, setFpOpen] = useState(false);
  const photos = (block.images || []).filter((i) => i.kind !== "floorplan");
  const floorplans = (block.images || []).filter((i) => i.kind === "floorplan");
  const fallbackPhoto = photos[0]?.url || null;
  return (
    <section className="mb-16 loc-section" data-testid={`proposal-location-${block.location}`}>
      <div className="flex items-baseline gap-3 mb-1">
        <span className="font-mono text-sm text-brand-gold">{String(index + 1).padStart(2, "0")}</span>
        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand-ink">{block.location}</h2>
      </div>
      {block.address && <p className="inline-flex items-center gap-1.5 text-sm text-brand-tertiary mb-5 ml-8"><MapPin className="h-3.5 w-3.5" /> {block.address}</p>}
      {block.description && <p className="text-lg text-brand-muted leading-relaxed max-w-[62ch] mb-7">{block.description}</p>}

      {(block.offices || []).length > 0 && (
        <div className="mb-10" data-testid={`proposal-offices-${block.location}`}>
          <div className="flex items-end justify-between gap-4 mb-5 pb-3 border-b-2 border-brand-ink">
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl font-light text-brand-ink">Suggested Offices &amp; Pricing</h3>
              <p className="text-[12px] text-brand-tertiary mt-1">Rates shown per month — longer terms unlock better pricing</p>
            </div>
            <span className="text-[11px] uppercase tracking-[0.15em] text-brand-gold whitespace-nowrap">{block.offices.length} option{block.offices.length === 1 ? "" : "s"}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {block.offices.map((o) => <OfficeCard key={o.id || o.office_number} o={o} fallback={fallbackPhoto} onSeen={onOfficeSeen} selectedTerm={selection?.[o.office_number]?.term} onToggleTerm={onToggleTerm} />)}
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-8" data-testid={`proposal-gallery-${block.location}`}>
          {photos.map((im, i) => (
            <figure key={im.id || i} className={`overflow-hidden border border-brand-border ${i === 0 ? "col-span-2 row-span-2 aspect-[4/3]" : "aspect-square"}`}>
              <img src={imgSrc(im.url)} alt={im.caption || block.location} crossOrigin="anonymous" className="w-full h-full object-cover" />
            </figure>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-8 mb-8">
        {(block.amenities || []).length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary mb-3 inline-flex items-center gap-2"><Wifi className="h-3.5 w-3.5" /> Amenities</p>
            <div className="flex flex-wrap gap-2">
              {block.amenities.map((a, i) => (
                <span key={i} className="text-[12px] text-brand-ink border border-brand-border bg-brand-surface rounded-full px-3 py-1">{a}</span>
              ))}
            </div>
          </div>
        )}
        {((block.conference_rooms_list || []).length > 0 || block.conference_rooms) && (
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary mb-3 inline-flex items-center gap-2"><DoorOpen className="h-3.5 w-3.5" /> Conference Rooms</p>
            {(block.conference_rooms_list || []).length > 0 ? (
              <div className="space-y-1.5" data-testid={`proposal-confrooms-${block.location}`}>
                {block.conference_rooms_list.map((r, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-3 border-b border-brand-border/70 pb-1.5 last:border-0">
                    <span className="text-sm text-brand-ink">{r.name}</span>
                    <span className="flex items-center gap-1.5 text-[12px] text-brand-muted whitespace-nowrap"><Users className="h-3.5 w-3.5 text-brand-tertiary" /> Seats up to {r.capacity}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-brand-muted leading-relaxed">{block.conference_rooms}</p>
            )}
          </div>
        )}
      </div>

      {floorplans.length > 0 && (
        <div className="mb-10" data-testid={`proposal-floorplan-${block.location}`}>
          <button onClick={() => setFpOpen((o) => !o)} data-testid={`proposal-floorplan-toggle-${block.location}`}
            className="fp-toggle w-full flex items-center justify-between gap-4 px-5 py-3.5 border border-brand-border bg-brand-surface hover:bg-brand-hover transition-colors">
            <span className="font-serif text-2xl font-light text-brand-ink">Floor Plan{floorplans.length > 1 ? "s" : ""}</span>
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-brand-tertiary">
              {fpOpen ? "Hide" : "View"} <ChevronDown className={`h-4 w-4 transition-transform ${fpOpen ? "rotate-180" : ""}`} />
            </span>
          </button>
          <div className="fp-collapsible grid gap-5 mt-5" style={{ display: fpOpen ? "grid" : "none" }}>
            {floorplans.map((im, i) => (
              <figure key={im.id || i} className="bg-white border border-brand-border shadow-sm break-inside-avoid">
                <figcaption className="flex items-center justify-between px-4 py-2.5 border-b border-brand-border bg-brand-surface">
                  <span className="font-mono text-[11px] text-brand-gold">{String(i + 1).padStart(2, "0")}</span>
                  <a href={imgSrc(im.url)} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-[0.2em] text-brand-tertiary hover:text-brand-primary">{im.caption || (im.pdf ? "Open floor plan PDF" : "Marketing floor plan")}</a>
                </figcaption>
                {im.pdf ? (
                  <iframe src={imgSrc(im.url)} title={`${block.location} floor plan ${i + 1}`} className="w-full h-[78vh] min-h-[560px] bg-white" />
                ) : (
                  <img src={imgSrc(im.url)} alt={im.caption || `${block.location} floor plan`} crossOrigin="anonymous" className="w-full h-auto object-contain p-4" />
                )}
              </figure>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function parseExpiry(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function fmtValid(v) {
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const ProposalView = forwardRef(function ProposalView({ proposal, onOfficeSeen, selection, onToggleTerm }, ref) {
  if (!proposal) return null;
  const { title, client_name, intro, notes, valid_until, account_exec, cover_url, location_blocks = [] } = proposal;
  const expiry = parseExpiry(valid_until);
  const today = new Date(new Date().toDateString());
  const expired = expiry && expiry < today;
  const heroFromLoc = location_blocks.map((b) => (b.images || []).find((i) => i.kind !== "floorplan")?.url).find(Boolean);
  const coverImg = cover_url || heroFromLoc;
  return (
    <div className="bg-brand-page text-brand-ink proposal-theme" data-testid="proposal-view">
      <div ref={ref} className="max-w-[880px] w-full mx-auto bg-brand-page">
        {expired && (
          <div data-testid="proposal-expired" className="bg-brand-gold/15 border-b border-brand-gold px-8 sm:px-14 py-3 text-center text-sm text-brand-ink">
            This proposal expired on {expiry.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}. Please reach out for an updated quote.
          </div>
        )}
        {/* Cover */}
        <header className="relative overflow-hidden bg-brand-ink text-brand-page border-b border-brand-gold/25">
          {coverImg && <img src={imgSrc(coverImg)} alt="" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative px-8 sm:px-14 py-12 sm:py-16">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-8 w-8 border border-brand-gold text-brand-gold flex items-center justify-center font-serif text-sm">B</span>
              <span className="font-serif text-lg tracking-tight">Bond Collective</span>
              <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-brand-page/70">Workspace Proposal</span>
            </div>
            {client_name && <p className="text-xs uppercase tracking-[0.24em] text-brand-gold mb-3">Prepared for {client_name}</p>}
            <h1 className="font-serif text-4xl sm:text-5xl font-light leading-[1.05] tracking-tight max-w-[18ch]">{title}</h1>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-6 text-sm text-brand-page/80">
              {(proposal.locations || []).length > 0 && <span>{proposal.locations.join(" · ")}</span>}
              {valid_until && <span className="text-brand-page/60">Valid until {fmtValid(valid_until)}</span>}
              {account_exec && <span className="text-brand-page/60">{account_exec}</span>}
            </div>
          </div>
        </header>

        <div className="px-8 sm:px-14 py-12">
          {/* Executive summary */}
          {intro && (
            <section className="mb-16" data-testid="proposal-intro">
              <div className="border-l-2 border-brand-gold pl-6">
                <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary mb-3">Overview</p>
                <p className="font-serif text-2xl font-light leading-snug text-brand-ink whitespace-pre-line">{intro}</p>
              </div>
            </section>
          )}

          {location_blocks.map((b, i) => <LocationSection key={b.location} block={b} index={i} onOfficeSeen={onOfficeSeen} selection={selection} onToggleTerm={onToggleTerm} />)}

          {onToggleTerm && (location_blocks || []).some((b) => (b.offices || []).length > 0) && (
            <section className="mb-12 mt-4" data-testid="proposal-request-banner">
              <div className="rounded-lg border border-brand-gold/45 bg-brand-gold/8 px-6 py-5">
                <p className="font-serif text-2xl font-light text-brand-ink">Found the right fit?</p>
                <p className="text-base text-brand-muted leading-relaxed mt-2 max-w-[68ch]">
                  Tap the term you'd like on any office above to <strong className="text-brand-ink">request an agreement</strong>. You can select more than one. Selecting an office is <strong className="text-brand-ink">not a commitment</strong> — it simply asks our team to send an agreement for the option(s) and term(s) you choose.
                </p>
                <p className="text-sm text-brand-tertiary mt-2">Nothing is finalized until an agreement is signed.</p>
              </div>
            </section>
          )}

          {notes && (
            <section className="mb-14" data-testid="proposal-notes">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary mb-3">Notes &amp; Inclusions</p>
              <p className="text-base text-brand-muted leading-relaxed whitespace-pre-line max-w-[68ch]">{notes}</p>
            </section>
          )}

          <footer className="border-t border-brand-border pt-6 mt-4 flex flex-wrap items-center justify-between gap-3 text-[11px] text-brand-tertiary">
            <span>Bond Collective · Confidential Proposal{client_name ? ` for ${client_name}` : ""}</span>
            {account_exec && <span>{account_exec}</span>}
          </footer>
        </div>
      </div>
    </div>
  );
});

export default ProposalView;
